// Form doldurma sağ paneli — §4.4.2 eksikler + §6.4 yardımcı bilgiler. FormFill'den ayrıldı
// (Finar UX P3 Ö6). #013: flexShrink: 0 + height: 100% → panel sütunu kaplar, içerik kendi
// içinde scroll. Split-window daralması (müşteri talebi): AppShell menü pattern'iyle
// daraltılabilir; ray modunda eksik sayısı rozeti sinyali korur. ≤1024 px'te daraltılmış
// açılır; açılınca formu itmez (overlay, 48 px yer tutucu), Escape/dış tıklama/gezinme kapatır.

import { useRef } from "react";
import { PanelRightClose, PanelRightOpen } from "lucide-react";
import { useAppStore } from "../../stores/app-store";
import { useFormFillStore } from "../../stores/form-fill-store";
import {
  OVERLAY_PANEL_STYLE,
  useOverlayDismiss,
  usePanelFocus,
} from "../../hooks/use-overlay-dismiss";
import { MissingPanel } from "./MissingPanel";

export function MissingPanelAside(): JSX.Element {
  const missingCount = useFormFillStore((s) => s.missingFields.length);
  const missingPanelCollapsed = useAppStore((s) => s.missingPanelCollapsed);
  const toggleMissingPanel = useAppStore((s) => s.toggleMissingPanel);
  const overlay = useAppStore((s) => s.narrowFormPanels);
  const panelRef = useRef<HTMLElement>(null);
  const openerRef = useRef<HTMLButtonElement>(null);
  const focus = usePanelFocus(missingPanelCollapsed, panelRef, openerRef);
  const close = (): void => {
    focus.markClose();
    toggleMissingPanel();
  };
  useOverlayDismiss(panelRef, overlay && !missingPanelCollapsed, toggleMissingPanel, close);

  if (missingPanelCollapsed) {
    return (
      <aside
        style={{
          flex: "0 0 48px",
          height: "100%",
          background: "var(--color-surface)",
          borderLeft: "1px solid var(--color-border)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          paddingTop: 12,
          gap: 12,
        }}
      >
        <button
          ref={openerRef}
          onClick={() => {
            focus.markOpen();
            toggleMissingPanel();
          }}
          aria-label="Eksik alanlar panelini aç"
          title="Eksik alanlar panelini aç"
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: "var(--color-text-3)",
            padding: 6,
          }}
        >
          <PanelRightOpen size={18} />
        </button>
        <span
          aria-label={
            missingCount > 0 ? `${missingCount.toString()} zorunlu alan boş` : "Tüm alanlar dolu"
          }
          title={
            missingCount > 0 ? `${missingCount.toString()} zorunlu alan boş` : "Tüm alanlar dolu"
          }
          style={{
            minWidth: 22,
            height: 22,
            borderRadius: 999,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 5px",
            fontFamily: "var(--font-mono)",
            fontSize: "0.7rem",
            fontWeight: 700,
            color: "white",
            background: missingCount > 0 ? "var(--color-error)" : "var(--color-success)",
          }}
        >
          {missingCount > 0 ? missingCount : "✓"}
        </span>
        <span
          style={{
            writingMode: "vertical-rl",
            fontFamily: "var(--font-mono)",
            fontSize: "0.68rem",
            letterSpacing: "0.08em",
            color: "var(--color-text-3)",
            textTransform: "uppercase",
          }}
        >
          Eksik Alanlar
        </span>
      </aside>
    );
  }

  const panel = (
    <aside
      ref={panelRef}
      style={{
        flex: "0 0 320px",
        height: "100%",
        background: "var(--color-surface)",
        borderLeft: "1px solid var(--color-border)",
        padding: 16,
        overflowY: "auto",
        ...(overlay ? { ...OVERLAY_PANEL_STYLE, right: 0, width: 320 } : {}),
      }}
    >
      <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "0.72rem",
            letterSpacing: "0.08em",
            color: "var(--color-text-3)",
            textTransform: "uppercase",
          }}
        >
          Eksik Alanlar
        </span>
        <button
          onClick={close}
          aria-label="Eksik alanlar panelini daralt"
          title="Eksik alanlar panelini daralt"
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: "var(--color-text-3)",
            padding: 2,
          }}
        >
          <PanelRightClose size={16} />
        </button>
      </div>
      <MissingPanel onNavigate={overlay ? toggleMissingPanel : undefined} />
    </aside>
  );
  if (!overlay) return panel;
  return <div style={{ width: 48, flexShrink: 0, position: "relative", zIndex: 20 }}>{panel}</div>;
}
